import { useState, useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { SideModal } from './SideModal'
import { FiUploadCloud, FiLoader, FiCheckCircle, FiAlertTriangle, FiArrowLeft, FiArrowRight } from 'react-icons/fi'

// Importa o plano de contas (ex.: LISTAGEM DE CLASSIFICAÇÃO FINANCEIRA do ERP)
// — mesmo wizard de 4 etapas de ImportarFavorecidos.tsx (parse genérico em
// /api/import/parse + commit específico em /api/categories/import/commit, que
// resolve a hierarquia sintético→analítico pelo código).

interface ParseResp { success: boolean; grid: string[][]; suggestedHeader: number; totalRows: number; sheetName: string; error?: string }
interface CommitResp { success: boolean; imported: number; skipped: number; failed: { line: number | string; error: string }[]; error?: string }

type ImportRow = { [k: string]: string | number | undefined; __line?: number }

const FIELDS: { key: string; label: string; required: boolean; hints: string[] }[] = [
  { key: 'codigo', label: 'Código',              required: true,  hints: ['codigo', 'código'] },
  { key: 'nome',   label: 'Classificação/Nome',  required: true,  hints: ['classifica', 'nome', 'descri'] },
  { key: 'nivel',  label: 'Nível (Sintético/Analítico)', required: true, hints: ['nivel', 'nível'] },
  { key: 'redz',   label: 'Cód. de redução (Redz.)', required: false, hints: ['redz', 'redu'] },
]

const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim()

export function ImportarCategorias({ onClose, onImported }: { onClose: () => void; onImported: () => void }) {
  const { authenticatedFetch } = useAuth()
  const [step, setStep] = useState(1)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [fileName, setFileName] = useState('')
  const [grid, setGrid] = useState<string[][]>([])
  const [headerRow, setHeaderRow] = useState(0)
  const [mapping, setMapping] = useState<Record<string, number>>({})
  const [result, setResult] = useState<CommitResp | null>(null)

  const headerCells = grid[headerRow] || []
  const dataRows = useMemo(() => grid.slice(headerRow + 1).filter(r => r.some(c => String(c).trim() !== '')), [grid, headerRow])
  const maxCols = useMemo(() => grid.reduce((m, r) => Math.max(m, r.length), 0), [grid])

  const autoMap = (hdr: string[]) => {
    const m: Record<string, number> = {}
    FIELDS.forEach(f => {
      const idx = hdr.findIndex(c => { const n = norm(c); return f.hints.some(h => n.includes(h)) })
      m[f.key] = idx
    })
    setMapping(m)
  }

  async function handleFile(file: File) {
    setBusy(true); setError(null)
    try {
      const fd = new FormData(); fd.append('file', file)
      const r = await authenticatedFetch('/api/import/parse', { method: 'POST', body: fd })
      const d: ParseResp = await r.json()
      if (!d.success) { setError(d.error || 'Falha ao ler a planilha'); return }
      setFileName(file.name)
      setGrid(d.grid)
      setHeaderRow(d.suggestedHeader)
      autoMap(d.grid[d.suggestedHeader] || [])
      setStep(2)
    } catch { setError('Erro ao enviar o arquivo') }
    finally { setBusy(false) }
  }

  const missingRequired = FIELDS.filter(f => f.required && (mapping[f.key] == null || mapping[f.key] < 0))

  const buildRows = (): ImportRow[] => dataRows.map(row => {
    const obj: ImportRow = {}
    FIELDS.forEach(f => { const ci = mapping[f.key]; if (ci != null && ci >= 0) obj[f.key] = row[ci] ?? '' })
    const realIdx = grid.indexOf(row)
    obj.__line = realIdx >= 0 ? realIdx + 1 : undefined
    return obj
  })

  async function commit() {
    setBusy(true); setError(null)
    try {
      const rows = buildRows()
      const r = await authenticatedFetch('/api/categories/import/commit', { method: 'POST', body: JSON.stringify({ rows }) })
      const d: CommitResp = await r.json()
      if (!d.success) { setError(d.error || 'Falha ao importar'); return }
      setResult(d)
      setStep(4)
      if (d.imported > 0) onImported()
    } catch { setError('Erro ao importar categorias') }
    finally { setBusy(false) }
  }

  const steps = ['Carregar', 'Cadastrar', 'Confirmar', 'Concluir']

  const footer = (
    <>
      {step > 1 && step < 4 && (
        <button onClick={() => { setError(null); setStep(s => s - 1) }} disabled={busy}
          className="neu-convex inline-flex items-center gap-2 rounded-lg border border-border h-9 px-3 text-sm hover:bg-secondary disabled:opacity-40">
          <FiArrowLeft className="w-4 h-4" /> Voltar
        </button>
      )}
      {step === 2 && (
        <button onClick={() => { setError(null); setStep(3) }} disabled={busy || missingRequired.length > 0}
          className="neu-relief inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground h-9 px-3 text-sm font-medium hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed">
          Continuar <FiArrowRight className="w-4 h-4" />
        </button>
      )}
      {step === 3 && (
        <button onClick={commit} disabled={busy || dataRows.length === 0}
          className="neu-relief inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground h-9 px-3 text-sm font-medium hover:opacity-90 disabled:opacity-40">
          {busy ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiCheckCircle className="w-4 h-4" />}
          Importar {dataRows.length} categoria{dataRows.length === 1 ? '' : 's'}
        </button>
      )}
      {step === 4 && (
        <button onClick={onClose} className="neu-relief rounded-lg bg-primary text-primary-foreground h-9 px-4 text-sm font-medium hover:opacity-90">Fechar</button>
      )}
    </>
  )

  return (
    <SideModal title="Importar plano de contas" subtitle={fileName || 'XLS, XLSX ou CSV até 10 MB'}
      icon={FiUploadCloud} onClose={onClose} maxWidth="max-w-2xl" footer={footer}>
      {/* PLANOS (21/10/2026): cada passo do assistente virou uma seção de papel
          (.pln-secao) com título 15px; selects e botões h-9, textos de apoio 13px. */}
      <div className="flex items-center gap-1.5 text-[13px]">
        {steps.map((s, i) => {
          const n = i + 1, done = n < step, cur = n === step
          return (
            <div key={s} className="flex items-center gap-1.5">
              <span className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold ${done ? 'bg-primary text-primary-foreground' : cur ? 'bg-primary/15 text-primary ring-1 ring-primary' : 'bg-secondary text-muted-foreground'}`}>{n}</span>
              <span className={cur ? 'text-foreground font-medium' : 'text-muted-foreground'}>{s}</span>
              {i < steps.length - 1 && <span className="w-4 h-px bg-border mx-0.5" />}
            </div>
          )
        })}
      </div>

      {error && <div className="text-sm text-red-500 flex items-center gap-2"><FiAlertTriangle className="w-4 h-4 shrink-0" />{error}</div>}

      {step === 1 && (
        <section className="pln-secao">
          <h4 className="pln-secao-titulo">Arquivo</h4>
          <p className="text-[13px] text-muted-foreground">
            Cada linha vira uma categoria. O <strong>Nível</strong> (Sintético/Analítico) define se é um grupo (mãe)
            ou uma folha — analíticos são ligados à mãe automaticamente pelo prefixo do código (ex.: "10.1" → mãe "10").
            O tipo (receita/despesa) é deduzido do nome do grupo sintético.
          </p>
          <label className="neu-well block border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-primary/50 hover:bg-secondary/30 transition-colors">
            <input type="file" accept=".xls,.xlsx,.csv" className="hidden" disabled={busy}
              onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f) }} />
            {busy ? <FiLoader className="w-8 h-8 mx-auto animate-spin text-muted-foreground" /> : <FiUploadCloud className="w-8 h-8 mx-auto text-muted-foreground" />}
            <div className="mt-2 text-sm text-foreground font-medium">Clique para selecionar o arquivo</div>
          </label>
        </section>
      )}

      {step === 2 && (
        <section className="pln-secao">
          <h4 className="pln-secao-titulo">Mapear colunas</h4>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Linha de cabeçalho:</span>
            <select value={headerRow} onChange={e => { const h = Number(e.target.value); setHeaderRow(h); autoMap(grid[h] || []) }}
              className="neu-convex rounded-md border border-border bg-card h-9 px-2.5 text-sm">
              {grid.slice(0, 15).map((r, i) => (
                <option key={i} value={i}>Linha {i + 1} — {r.filter(c => String(c).trim()).slice(0, 3).join(', ').slice(0, 40) || '(vazia)'}</option>
              ))}
            </select>
          </div>
          <p className="text-[13px] text-muted-foreground">Relacione cada campo do sistema a uma coluna da planilha. Campos com <span className="text-red-500">*</span> são obrigatórios.</p>
          <div className="space-y-2">
            {FIELDS.map(f => (
              <div key={f.key} className="flex items-center gap-3">
                <label className="text-sm w-52 shrink-0 text-foreground">{f.label}{f.required && <span className="text-red-500"> *</span>}</label>
                <select value={mapping[f.key] ?? -1} onChange={e => setMapping(m => ({ ...m, [f.key]: Number(e.target.value) }))}
                  className={`flex-1 rounded-md border bg-card h-9 px-2.5 text-sm ${f.required && (mapping[f.key] ?? -1) < 0 ? 'border-red-400' : 'border-border'}`}>
                  <option value={-1}>— ignorar —</option>
                  {Array.from({ length: maxCols }, (_, ci) => (
                    <option key={ci} value={ci}>{headerCells[ci]?.trim() || `Coluna ${ci + 1}`}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
          {missingRequired.length > 0 && (
            <div className="text-[13px] text-amber-600 dark:text-amber-400">Falta mapear: {missingRequired.map(f => f.label).join(', ')}.</div>
          )}
        </section>
      )}

      {step === 3 && (
        <section className="pln-secao">
          <h4 className="pln-secao-titulo">Conferir antes de importar</h4>
          <p className="text-sm text-muted-foreground">{dataRows.length} linha{dataRows.length === 1 ? '' : 's'} pronta{dataRows.length === 1 ? '' : 's'} para importar. Já existentes (mesmo código+nome) são ignoradas automaticamente.</p>
          <div className="border border-border rounded-lg overflow-x-auto max-h-[46vh]">
            <table className="w-full text-[13px] min-w-[480px]">
              <thead className="sticky top-0 bg-card">
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="px-2.5 py-2 font-medium">Código</th>
                  <th className="px-2.5 py-2 font-medium">Nome</th>
                  <th className="px-2.5 py-2 font-medium">Nível</th>
                  <th className="px-2.5 py-2 font-medium">Redz.</th>
                </tr>
              </thead>
              <tbody>
                {buildRows().slice(0, 100).map((r, i) => (
                  <tr key={i} className="border-b border-border/50 last:border-0">
                    <td className="px-2.5 py-2 whitespace-nowrap font-mono">{r.codigo || '—'}</td>
                    <td className="px-2.5 py-2 truncate max-w-[220px]">{r.nome || '—'}</td>
                    <td className="px-2.5 py-2 whitespace-nowrap">{r.nivel || '—'}</td>
                    <td className="px-2.5 py-2 whitespace-nowrap">{r.redz || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {dataRows.length > 100 && <p className="text-[13px] text-muted-foreground">Mostrando as 100 primeiras de {dataRows.length}.</p>}
        </section>
      )}

      {step === 4 && result && (
        <section className="pln-secao">
          <h4 className="pln-secao-titulo">Resultado</h4>
          <div className="flex items-center gap-3">
            <FiCheckCircle className="w-8 h-8 text-primary shrink-0" />
            <div>
              <div className="text-lg font-semibold text-foreground">{result.imported} categoria{result.imported === 1 ? '' : 's'} importada{result.imported === 1 ? '' : 's'}</div>
              {result.skipped > 0 && <div className="text-sm text-muted-foreground">{result.skipped} já existente{result.skipped === 1 ? '' : 's'} (ignorada{result.skipped === 1 ? '' : 's'})</div>}
              {result.failed.length > 0 && <div className="text-sm text-amber-600 dark:text-amber-400">{result.failed.length} linha{result.failed.length === 1 ? '' : 's'} com erro (veja abaixo)</div>}
            </div>
          </div>
          {result.failed.length > 0 && (
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="px-3 py-2 text-xs font-medium text-muted-foreground bg-secondary/40 border-b border-border">Linhas não importadas</div>
              <div className="max-h-[40vh] overflow-auto divide-y divide-border/50">
                {result.failed.map((f, i) => (
                  <div key={i} className="px-3 py-2 text-sm flex gap-2">
                    <span className="text-muted-foreground shrink-0">Linha {f.line}:</span>
                    <span className="text-foreground">{f.error}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}
    </SideModal>
  )
}
